import { ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import InfoIcon from '@mui/icons-material/Info';
import Badge from 'components/common/Badge/Badge';
import Button from 'components/common/Button/Button';
import { TableRow } from 'components/common/Table/Table';

import ELEMENT from 'common/element';
import { generateRandomHash } from 'common/util/generator';
import { ACCESS_OPTIONS_MAP, USER_STATUSES } from './consts';
import { Mutation, RowData, USER_SOURCE, USER_STATUS, UserFormAction } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createAction = (type: string, payload?: any): UserFormAction => ({ type, payload });

export const makeRowData = ({
  email,
  access,
  status,
  source,
  isWriteDisabled,
  resendInvite,
  revokeInvite,
  revokeAccess,
  refetchData,
}: RowData): TableRow => {
  // user is provisioned via an external source such as OKTA/AZURE SCIM groups.
  const isExternalSource = source === USER_SOURCE.SCIM;
  const onActionRefreshData = async (callback: Mutation): Promise<void> => {
    const { data } = await callback({ variables: { email } });
    if (data) {
      await refetchData();
    }
  };

  const tooltipIcon = (): ReactNode | undefined => {
    if (isExternalSource) {
      const hash = generateRandomHash();
      return (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`TeamTable-InfoTooltip-${email}-${hash}`}>User provisioned via an external system</Tooltip>
          }
        >
          <InfoIcon className="TeamTable__InfoIcon" htmlColor="#848797" />
        </OverlayTrigger>
      );
    }
    return undefined;
  };

  return {
    emailValue: email,
    access: ACCESS_OPTIONS_MAP[access],
    email: (
      <div className="TeamTable__UserSection">
        <Badge variant={USER_STATUSES[status].variant} className="TeamTable__UserSectionBadge">
          {USER_STATUSES[status].name}
        </Badge>
        <div className="TeamTable__Email" data-test-id={ELEMENT.userAccountsTeamTableEmailCell}>
          {email}
        </div>
      </div>
    ),
    actions: (
      <>
        {status === USER_STATUS.INVITED && (
          <>
            <Button
              size="sm"
              onClick={(): void => {
                onActionRefreshData(resendInvite);
              }}
              testId={ELEMENT.userAccountsResendInviteButton}
              disabled={isExternalSource || isWriteDisabled}
            >
              Resend Invite
            </Button>
            <Button
              size="sm"
              className="TeamTable__RevokeButton"
              onClick={(): Promise<void> => onActionRefreshData(revokeInvite)}
              testId={ELEMENT.userAccountsRevokeInviteButton}
              disabled={isExternalSource || isWriteDisabled}
            >
              Revoke Invite
            </Button>
          </>
        )}
        {status === USER_STATUS.ACTIVE && (
          <Button
            size="sm"
            onClick={(): Promise<void> => onActionRefreshData(revokeAccess)}
            testId={ELEMENT.userAccountsRevokeAccessButton}
            disabled={isExternalSource || isWriteDisabled}
          >
            Revoke Access
          </Button>
        )}
        {tooltipIcon()}
      </>
    ),
  };
};
